import React, { useState, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "../components/Input";
import Label from "../components/Label";
import { useReduxDispatch, useReduxSelector } from "../redux/hooks";
import { UserAPI } from "../middleware";
import { Alert } from "@mui/material";
import { LoadingModal } from "../components/Modal";
import { DatePicker, DayCalendarSkeleton, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import { style } from '../utils/styling';
import { SignupFailed, SignupPending, SignupSuccessful } from "../redux/slice/SignupSlice";
import { BsChatLeftDotsFill } from "react-icons/bs";
import OTPInput from 'react-otp-input';

type Inputs = {
  username: string;
  patientNumber: string;
  dateOfBirth: string;
};

type ErrorItem = {
  message: string;
  field?: string;
};

const OTPInputScreen = () => {
  const { isSignedIn } = useReduxSelector((state) => state.user);
  const { isLoading } = useReduxSelector((state) => state.signup);
  const signupData = useReduxSelector((state) => state.signup.user);
  const [timer, setTimer] = React.useState<any>(null);
  const id = React.useRef<any>(null);

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<ErrorItem[]>([]);
  const [otp, setOtp] = useState("");
  const [selectedNumber, setSelectedNumber] = useState<string>("");
  const [signUpResponseData, setSignUpResponseData] = useState<any>(null);
  const [signUpToken, setSignUpToken] = useState<string>("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [dataSource, setDataSource] = useState<string>("none");


  const [debugData, setDebugData] = useState<any>({});
  const [showDebug, setShowDebug] = useState(false);

  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const location = useLocation();
  const { state } = location;

  const handleClose = () => setOpen(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  const sendOTP = async () => {
    try {
      setOpen(true);


      let contactNumber = selectedNumber;

      if (!contactNumber) {
        contactNumber = state?.patientContact ||
          localStorage.getItem('patientContact') ||
          (signUpResponseData?.data?.[0]?.patientContacts?.[0]?.value);
      }

      if (!contactNumber) {
        setOpen(false);
        setError([{ message: "No contact number available to send OTP. Please sign up again." }]);
        return null;
      }


      contactNumber = contactNumber.replace(/\*/g, "");
      console.log("Sending OTP to:", contactNumber);


      const formattedContact = contactNumber.startsWith('0') ? contactNumber : `0${contactNumber}`;


      let token = signUpToken;
      if (!token) {
        if (signUpResponseData?.data?.[0]?.signUpToken) {
          token = signUpResponseData.data[0].signUpToken;
        } else {
          token = localStorage.getItem('signUpToken') || "";
        }
      }

      if (!token) {
        setOpen(false);
        setError([{ message: "Missing signup token for verification" }]);
        return null;
      }

      setSignUpToken(token);
      localStorage.setItem('signUpToken', token);


      const response = await UserAPI.sendOTP(token, formattedContact);
      console.log("OTP sent successfully:", response.data);


      if (response?.data?.data?.[0]?.initiateRequest?.token) {
        const newToken = response.data.data[0].initiateRequest.token;
        setSignUpToken(newToken);
        localStorage.setItem('signUpToken', newToken);
      }

      setIsOtpSent(true);
      setOpen(false);
      setSelectedNumber(formattedContact);

      return response;
    } catch (error: any) {
      console.error("Failed to send OTP:", error);
      const errorMessage: ErrorItem[] = error?.response?.data?.errors || [{ message: error.message || "Failed to send OTP" }];
      setError(errorMessage);
      setOpen(false);
      return null;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeOtp = async () => {
      try {
        console.log("Initializing OTP screen...");
        setOpen(true);


        const urlParams = new URLSearchParams(window.location.search);
        const txnRef = urlParams.get('txn_ref') || localStorage.getItem('txnRef');
        const clientRef = urlParams.get('client_ref') || localStorage.getItem('clientRef');
        const signupDataParam = urlParams.get('signup_data');
        const savedData = localStorage.getItem('signUpData');
        const contactNumber = localStorage.getItem('patientContact');
        const storedToken = localStorage.getItem('signUpToken');


        const paymentRawData = localStorage.getItem('paymentRawData');


        const debugInfo = {
          urlParams: Object.fromEntries(urlParams.entries()),
          txnRef,
          clientRef,
          contactNumber,
          storedToken,
          paymentRawData: paymentRawData ? JSON.parse(paymentRawData) : null,
          state: state || null,
        };

        setDebugData(debugInfo);


        console.log("Redux signup state:", signupData);

        console.log("Available data sources:", {
          txnRef: txnRef || "none",
          clientRef: clientRef || "none",
          navigationState: state ? "present" : "none",
          urlParam: signupDataParam ? "present" : "none",
          localStorage: savedData ? "present" : "none",
          contactNumber: contactNumber || "none",
          signUpToken: storedToken || "none",
          reduxState: signupData ? "present" : "none",
          paymentRawData: paymentRawData ? "present" : "none"
        });


        if (storedToken) {
          console.log("Found signup token in localStorage:", storedToken);
          setSignUpToken(storedToken);
        } else if (state?.signUpToken) {
          console.log("Found signup token in navigation state:", state.signUpToken);
          setSignUpToken(state.signUpToken);
          localStorage.setItem('signUpToken', state.signUpToken);
        }


        if (contactNumber) {
          console.log("Found contact number:", contactNumber);
          setSelectedNumber(contactNumber);
        }


        let signUpData = state?.signUpResponseData;
        if (signUpData) {
          console.log("Found signup data in navigation state:", signUpData);
          setSignUpResponseData(signUpData);
          setDataSource("navigation");


          if (signUpData.data && signUpData.data[0]?.signUpToken) {
            const token = signUpData.data[0].signUpToken;
            setSignUpToken(token);
            localStorage.setItem('signUpToken', token);
          }
        }


        if (!signUpData && signupDataParam) {
          try {
            signUpData = JSON.parse(decodeURIComponent(signupDataParam));
            console.log("Found signup data in URL parameter:", signUpData);
            setSignUpResponseData(signUpData);
            setDataSource("url");


            if (signUpData.data && signUpData.data[0]?.signUpToken) {
              const token = signUpData.data[0].signUpToken;
              setSignUpToken(token);
              localStorage.setItem('signUpToken', token);
            }
          } catch (e) {
            console.error("Error parsing URL signup data:", e);
          }
        }


        if (!signUpData && savedData) {
          try {
            signUpData = JSON.parse(savedData);
            console.log("Retrieved signup data from localStorage:", signUpData);
            setSignUpResponseData(signUpData);
            setDataSource("localStorage");


            if (signUpData.data && signUpData.data[0]?.signUpToken) {
              const token = signUpData.data[0].signUpToken;
              setSignUpToken(token);
              localStorage.setItem('signUpToken', token);
            }
          } catch (e) {
            console.error("Error parsing saved signup data:", e);
          }
        }


        if (!signUpData && signupData && signupData.new_signup) {
          console.log("Using signup data from Redux state");
          signUpData = signupData.new_signup;
          setSignUpResponseData(signUpData);
          setDataSource("redux");


          if (signUpData.data && signUpData.data[0]?.signUpToken) {
            const token = signUpData.data[0].signUpToken;
            setSignUpToken(token);
            localStorage.setItem('signUpToken', token);
          }
        }


        if (!signUpData && signUpToken) {
          console.log("Using token only for OTP:", signUpToken);
          setDataSource("token");


          const result = await sendOTP();
          if (result && isMounted) {
            setOpen(false);
            return;
          }
        }


        if ((signUpData || signUpToken) && isMounted) {

          if (signUpData && dataSource !== "localStorage") {
            localStorage.setItem('signUpData', JSON.stringify(signUpData));
          }


          if (!selectedNumber && signUpData?.data?.[0]?.patientContacts?.[0]?.value) {
            const derivedContact = signUpData.data[0].patientContacts[0].value;
            console.log("Setting derived contact number:", derivedContact);
            setSelectedNumber(derivedContact);
            localStorage.setItem('patientContact', derivedContact);
          }

          setOpen(false);
          await sendOTP();
        } else if (isMounted) {
          console.error("No signup data or token found from any source");
          setOpen(false);
          setError([{
            message: "No signup data found. Please try signing up again or check your payment status."
          }]);


          setTimeout(() => {
            navigate("/signup", { replace: true });
          }, 5000);
        }
      } catch (error: any) {
        if (isMounted) {
          console.error("Error in initializeOtp:", error);
          setError([{ message: error.message || "Failed to initialize OTP process" }]);
          setOpen(false);
        }
      }
    };

    initializeOtp();

    return () => {
      isMounted = false;
    };
  }, []);

  const onSubmit: SubmitHandler<Inputs> = async () => {
    if (!signUpToken) {
      setError([{ message: "Missing signup token. Please try again." }]);
      return;
    }

    setOpen(true);
    dispatch(SignupPending());

    try {
      console.log("Verifying OTP with:", {
        token: signUpToken,
        otp: otp,
        selectedNumber: selectedNumber
      });

      const response = await UserAPI.verifyOTP(signUpToken, otp);
      console.log("OTP Verification response:", response.data);

      if (response.status === 200) {
        console.log("Verification successful:", response.data);
        setOpen(false);


        let userData = {};
        if (response.data && response.data.data && response.data.data[0]) {
          userData = response.data.data[0].user || {};
        }


        const verifiedData = {
          patientContact: selectedNumber,
          signUpResponseData: signUpResponseData,
          user: userData,
          otp: otp,
          signUpToken: signUpToken
        };

        localStorage.setItem('verifiedSignupData', JSON.stringify(verifiedData));

        navigate("/set-new-password", {
          state: verifiedData
        });
      } else {
        throw new Error('Failed to verify OTP');
      }
    } catch (error: any) {
      const errorMessage: ErrorItem[] = error?.response?.data?.errors || [{ message: error.message || "Invalid OTP or verification failed" }];
      dispatch(SignupFailed(errorMessage));
      setOpen(false);
      setError(errorMessage);
    }
  };

  const handleChangeOtp = (otp: string) => {
    setOtp(otp);
  };

  const handleResendOTP = () => {
    if (signUpToken) {
      sendOTP();
    } else {
      setError([{ message: "Missing signup data. Please try signing up again." }]);
    }
  };


  const toggleDebug = () => {
    setShowDebug(!showDebug);
  };

  return (
    <>
      {!isSignedIn ? (
        <div className="py-8 md:py-0 px-8 md:px-14 h-screen grid grid-cols-1 md:grid-cols-2 gap-6 items-center relative overflow-x-hidden">
          <div className="flex flex-col justify-center z-10">
            <div className="flex justify-center items-center">
              <img
                src="/assets/trust-hospital-logo.png"
                alt="hospital logo"
                className="w-72 cursor-pointer"
                onClick={() => navigate("/")}
              />
            </div>
            <div className="px-6 md:px-10 border shadow-sm border-tertiary-200 rounded-xl flex flex-col justify-center py-6 mt-6 max-w-xl">
              <h1 className="text-4xl font-medium text-center">One Time Passcode</h1>
              {error.length > 0 &&
                error.map((item, index) => (
                  <div key={index}>
                    <Alert severity="error" className="mt-2">
                      {item.message}
                    </Alert>
                  </div>
                ))}

              <img
                src="/assets/otppadlock.svg"
                alt="hospital logo"
                className="w-28 pt-8 cursor-pointer m-auto"
              />
              <div className="text-xl text-center font-light">
                {isOtpSent ?
                  `Please enter the 4-digit code sent to your phone.` :
                  `Sending verification code to your phone...`}
              </div>
              <div className="mt-8 w-full flex flex-col items-center">
                <OTPInput
                  value={otp}
                  onChange={handleChangeOtp}
                  numInputs={4}
                  renderInput={(inputProps: any, index: any) => <input {...inputProps} />}
                  shouldAutoFocus={true}
                  inputStyle={{
                    width: "2rem",
                    height: "2rem",
                    margin: "0 0.5rem",
                    fontSize: "1rem",
                    borderRadius: "4px",
                    border: "1px solid rgba(0,0,0,0.3)"
                  }}
                  containerStyle={{
                    justifyContent: "center"
                  }}
                />
                <button
                  className={`w-full mt-6 h-10 rounded-2xl font-medium text-md ${otp.length === 4 ? 'bg-primary-50 text-white hover:opacity-90' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                  onClick={otp.length === 4 ? handleSubmit(onSubmit) : undefined}
                  disabled={otp.length !== 4}
                >
                  Continue
                </button>
                <button
                  className="text-primary-50 hover:underline mt-4"
                  onClick={handleResendOTP}
                >
                  Resend Code
                </button>

                <div className="mt-3 w-full">
                  <div onClick={toggleDebug} className="text-xs text-gray-400 text-center cursor-pointer select-none">
                    Debug Info (tap to {showDebug ? "hide" : "show"})
                  </div>

                  {showDebug && (
                    <div className="mt-2 p-2 bg-gray-100 rounded-md text-xs text-left overflow-auto max-h-40">
                      <div><strong>Data Source:</strong> {dataSource}</div>
                      <div><strong>Phone:</strong> {selectedNumber || "Not set"}</div>
                      <div><strong>Signup Token:</strong> {signUpToken || "None"}</div>
                      <div><strong>Transaction Ref:</strong> {debugData.txnRef || "None"}</div>
                      <div><strong>Client Ref:</strong> {debugData.clientRef || "None"}</div>
                      <hr className="my-1 border-gray-300" />
                      <div><strong>Payment Data:</strong></div>
                      <pre className="text-xs">{JSON.stringify(debugData.paymentRawData, null, 2)}</pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="h-full w-full">
            <img
              src={"/assets/manwithbluebg.png"}
              alt="sign up illustration"
              className="w-full h-full object-contain absolute left-[400px] 2xl:left-[500px] 3xl:left-[550px]"
            />
          </div>
          <LoadingModal
            open={open}
            handleClose={handleClose}
            isLoading={isLoading}
          />
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default OTPInputScreen;
